import { useEffect, useState, type ReactNode } from "react";
import type { OnboardingPreview } from "../../services/OnboardingPreviewService";
import { useTranslation } from "../../context/LanguageContext";
import { dealsFromPreview, previewHasLocalDeals, previewPlace, type OnbDeal } from "../../utils/onboardingCoverage";
import {
  C,
  DealGridCard,
  DealGridSkeleton,
  GreenPill,
  ONB_YELLOW,
  ONB_YELLOW_INK,
  OnbError,
  OnbPage,
  OnbTopBar,
  YellowCta,
} from "./DealsFirstParts";

export interface PlanOption {
  id: string;
  name: string;
  /** Price charged for the first month (the intro price when discounted). */
  price: string;
  /** Full monthly price, struck through when discounted; null otherwise. */
  wasPrice: string | null;
  description: string;
}

interface Props {
  zip: string;
  /** undefined while loading, null when there is no local preview. */
  preview: OnboardingPreview | null | undefined;
  examples: OnboardingPreview | null | undefined;
  plans: PlanOption[];
  activePlanId: string;
  onSelectPlan: (id: string) => void;
  offerPct: number | null;
  ctaLabel: string;
  onContinue: () => void;
  busy?: boolean;
  error: string | null;
  canChooseFree: boolean;
  onFree: () => void;
  fine: ReactNode;
  onSignOut: () => void;
  onChangeZip: () => void;
}

export function useIsDesktop(query = "(min-width: 1024px)"): boolean {
  const get = () => (typeof window !== "undefined" && typeof window.matchMedia === "function" ? window.matchMedia(query).matches : false);
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const list = window.matchMedia(query);
    const onChange = () => setMatches(list.matches);
    onChange();
    list.addEventListener?.("change", onChange);
    return () => list.removeEventListener?.("change", onChange);
  }, [query]);
  return matches;
}

function Chip({ children, active = false }: { children: ReactNode; active?: boolean }) {
  return (
    <span
      className="flex h-8 flex-none items-center whitespace-nowrap rounded-full border-[1.5px] px-3 text-[13px] font-bold lg:h-[34px] lg:px-3.5 lg:text-[13.5px]"
      style={{ borderColor: active ? C.selBorder : C.line, backgroundColor: active ? C.sel : C.card, color: active ? C.icon : C.body }}
    >
      {children}
    </span>
  );
}

function ExamplesPill() {
  const { t } = useTranslation();
  return (
    <span className="flex h-[22px] items-center rounded-md px-2 text-[10.5px] font-extrabold uppercase tracking-[0.5px] lg:h-6 lg:rounded-[7px] lg:px-[9px] lg:text-[11.5px]" style={{ backgroundColor: C.chip, color: C.muted }}>
      {t("onboarding2.dp_examples")}
    </span>
  );
}

/** What the left column shows: this ZIP's deals, this week's leads, or labelled examples. */
export function usePlanDeals(preview: OnboardingPreview | null | undefined, examples: OnboardingPreview | null | undefined) {
  const { t, currentLanguage } = useTranslation();
  const number = (n: number) => n.toLocaleString(currentLanguage === "es" ? "es-US" : "en-US");
  if (preview === undefined) return { kind: "loading" as const, title: "", deals: undefined, count: 0, chips: [] as string[] };
  if (preview && previewHasLocalDeals(preview)) {
    const own = preview.mode === "own";
    const n = preview.stores.length;
    const title = own
      ? t("onboarding2.dp_title_own", { count: number(preview.dealCount) })
      : t(n === 1 ? "onboarding2.dp_title_nearby_one" : "onboarding2.dp_title_nearby", { count: number(preview.dealCount), stores: n });
    const storeChips = preview.stores.map((store) => `#${store.storeId} · ${Math.max(1, Math.round(store.distanceMiles))} mi`);
    const chips = own ? storeChips.slice(0, 1) : [t(n === 1 ? "onboarding2.dp_chip_walmarts_one" : "onboarding2.dp_chip_walmarts", { count: n }), ...storeChips];
    // Payback in the shopper's terms: markdown dollars here today, and how many
    // single finds save more than a month of the cheapest plan.
    const money = (n: number) => `$${Math.round(n).toLocaleString(currentLanguage === "es" ? "es-US" : "en-US")}`;
    const sub =
      preview.savingsTotal <= 0
        ? ""
        : preview.paybackDeals > 0
          ? t("onboarding2.dp_payback", { total: money(preview.savingsTotal), count: number(preview.paybackDeals) })
          : t("onboarding2.dp_payback_total", { total: money(preview.savingsTotal) });
    return { kind: "local" as const, title, sub, deals: dealsFromPreview(preview), count: preview.dealCount, chips };
  }
  if (preview?.mode === "leads" && preview.leads.length > 0) {
    return {
      // No crawled store near this ZIP: say so, promise the first scan, and show
      // this week's nationwide finds as proof rather than as "near you".
      kind: "leads" as const,
      title: t("onboarding2.preview_leads_title"),
      sub: t("onboarding2.preview_leads_sub"),
      deals: dealsFromPreview(preview),
      count: 0,
      chips: [t("onboarding2.dp_chip_leads_week", { count: number(preview.leadCountWeek) })],
    };
  }
  const place = previewPlace(examples);
  const exampleDeals: OnbDeal[] | undefined =
    examples === undefined ? undefined : dealsFromPreview(examples, place ? t("onboarding2.dp_example_store", { place }) : "");
  return {
    kind: "examples" as const,
    title: t("onboarding2.dp_title_generic"),
    deals: exampleDeals,
    count: 0,
    chips: [] as string[],
    examplesLabel: place ? t("onboarding2.dp_examples_from", { place }) : t("onboarding2.dp_examples_nationwide"),
  };
}

/**
 * Step 2, "deals first": the shopper's own local clearance (prices on the first
 * row, the rest behind a members lock) beside the plan picker. On phones the
 * plan picker is a fixed bottom sheet over a two-column grid.
 */
export default function PlansStepScreen(props: Props) {
  const { t } = useTranslation();
  const desktop = useIsDesktop();
  const view = usePlanDeals(props.preview, props.examples);
  // The first deals are shown in full (price, aisle, stock) before any price is asked.
  const pricedCount = 3;
  // Whole rows only: 4 columns on desktop, 2 on phones.
  const perRow = desktop ? 4 : 2;
  const cardCount = view.deals ? Math.min(desktop ? 8 : 6, view.deals.length < perRow ? view.deals.length : view.deals.length - (view.deals.length % perRow)) : 0;
  const cards = view.deals ? view.deals.slice(0, cardCount) : undefined;
  const active = props.plans.find((plan) => plan.id === props.activePlanId) ?? props.plans[0];

  const eyebrow = (
    <span className="flex items-center gap-2 text-[12px] font-extrabold uppercase tracking-[1px] lg:text-[13px]" style={{ color: C.icon }}>
      {t("onboarding2.near", { zip: props.zip })}
      <span aria-hidden="true" style={{ color: C.soft }}>·</span>
      <button type="button" onClick={props.onChangeZip} className="uppercase underline-offset-2 hover:underline" style={{ color: C.muted }}>
        {t("onboarding2.change")}
      </button>
    </span>
  );

  const grid = cards === undefined ? (
    <DealGridSkeleton count={desktop ? 8 : 4} columns="grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-[18px]" />
  ) : cards.length > 0 ? (
    <div className="relative">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-[18px]" data-testid="plan-deals-grid">
        {cards.map((deal, index) => (
          <DealGridCard key={deal.key} deal={deal} locked={index >= pricedCount} />
        ))}
      </div>
      {desktop && cards.length > pricedCount ? (
        <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-[-2px] h-[170px]" style={{ background: `linear-gradient(to bottom, transparent, ${C.page} 92%)` }} />
      ) : null}
    </div>
  ) : null;

  const left = (
    <section className="onb-rise min-w-0 flex-1 lg:max-w-[780px]">
      {eyebrow}
      {view.kind === "loading" ? (
        <div className="mt-2 h-[30px] w-[70%] animate-pulse rounded-lg lg:h-[38px]" style={{ backgroundColor: C.skeleton }} />
      ) : (
        <h1 className="m-0 mt-[3px] text-[25px] font-black leading-[28px] tracking-[-0.7px] lg:mt-1 lg:text-[34px] lg:leading-[40px] lg:tracking-[-1px]">
          {view.title}
        </h1>
      )}
      {(view.kind === "leads" || view.kind === "local") && view.sub ? (
        <p className="m-0 mt-2 max-w-[560px] text-[14px] leading-[20px] lg:text-[16px] lg:leading-[23px]" style={{ color: C.muted }}>
          {view.sub}
        </p>
      ) : null}
      {view.kind === "examples" ? (
        <div className="mt-3 flex items-center gap-2 lg:mt-4">
          <ExamplesPill />
          <b className="text-[14px] lg:text-[16px]">{view.examplesLabel}</b>
        </div>
      ) : view.chips.length > 0 ? (
        <div className="-mx-[18px] mt-3 flex gap-[7px] overflow-x-auto px-[18px] [scrollbar-width:none] lg:mx-0 lg:mt-3.5 lg:flex-wrap lg:gap-2 lg:overflow-visible lg:px-0">
          {view.chips.map((chip, index) => (
            <Chip key={chip} active={index === 0}>{chip}</Chip>
          ))}
        </div>
      ) : null}
      <div className="mt-3.5 lg:mt-5">{grid}</div>
    </section>
  );

  const planRows = (
    <div role="radiogroup" aria-label={t("onboarding2.choose_plan_label")} className="flex flex-col gap-3">
      {props.plans.map((plan) => {
        const selected = plan.id === active.id;
        return (
          <button
            key={plan.id}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={`${plan.name} ${plan.price}`}
            onClick={() => props.onSelectPlan(plan.id)}
            disabled={props.busy}
            className="flex items-center gap-3 rounded-2xl border-2 p-3.5 text-left transition hover:brightness-[0.99]"
            style={{ borderColor: selected ? C.selBorder : C.line, backgroundColor: selected ? C.sel : C.card }}
          >
            <span className="flex h-[22px] w-[22px] flex-none items-center justify-center rounded-full border-2" style={{ borderColor: selected ? C.selBorder : C.line }}>
              {selected ? <span className="h-3 w-3 rounded-full" style={{ backgroundColor: C.selBorder }} /> : null}
            </span>
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="flex items-center text-[16px] font-extrabold" style={{ color: C.ink }}>
                {plan.name}
                {plan.id === "pro" ? (
                  <span className="ml-1.5 inline-flex h-5 items-center rounded-md px-[7px] text-[10px] font-black" style={{ backgroundColor: ONB_YELLOW, color: ONB_YELLOW_INK }}>
                    {t("onboarding2.dp_best_value")}
                  </span>
                ) : null}
              </span>
              <span className="text-[12px] leading-[15px]" style={{ color: C.muted }}>{plan.description}</span>
            </span>
            <span className="flex flex-none flex-col items-end">
              <b className="text-[18px]" style={{ color: C.ink }}>{plan.price}</b>
              {plan.wasPrice ? <s className="text-[11.5px]" style={{ color: C.soft }}>{plan.wasPrice}</s> : null}
            </span>
          </button>
        );
      })}
    </div>
  );

  const pennyLine = (
    <span className="flex items-center justify-center gap-2 text-[13.5px]" style={{ color: C.body }}>
      <img src="/penny-meals-mark.png" alt="" className="h-5 w-5 object-contain" />
      <span>
        {t("onboarding2.dp_penny_prefix")} <b style={{ color: C.ink }}>Penny Meals Plus</b>
      </span>
    </span>
  );

  const freeLink = props.canChooseFree ? (
    <button
      type="button"
      onClick={props.onFree}
      disabled={props.busy}
      className="self-center px-2 py-1 text-[14px] font-semibold hover:underline"
      style={{ color: C.muted }}
    >
      {t("onboarding2.free_link")}
    </button>
  ) : null;

  const finePrint = (
    <p className="m-0 text-center text-[12px] leading-[17px]" style={{ color: C.soft }}>{props.fine}</p>
  );

  if (desktop) {
    return (
      <OnbPage testId="paywall-plan">
        <OnbTopBar onSignOut={props.onSignOut} />
        <main className="mx-auto flex w-full max-w-[1280px] items-start gap-10 px-10 pb-16 pt-10">
          {left}
          <aside
            className="onb-rise sticky top-6 flex w-[380px] flex-none flex-col gap-3 rounded-[26px] border p-[26px]"
            style={{ backgroundColor: C.card, borderColor: C.line, boxShadow: C.shadow, animationDelay: "60ms" }}
          >
            {props.offerPct !== null ? (
              <span className="self-start">
                <GreenPill>{t("onboarding2.offer_badge", { pct: props.offerPct })}</GreenPill>
              </span>
            ) : null}
            <h2 className="m-0 text-[24px] font-black leading-[28px] tracking-[-0.6px]">{t("onboarding2.dp_panel_title")}</h2>
            <OnbError error={props.error} />
            {planRows}
            {pennyLine}
            <YellowCta height={54} fontSize={17} onClick={props.onContinue} busy={props.busy}>
              {props.ctaLabel}
            </YellowCta>
            {freeLink}
            {finePrint}
          </aside>
        </main>
      </OnbPage>
    );
  }

  return (
    <OnbPage testId="paywall-plan">
      <OnbTopBar onSignOut={props.onSignOut} />
      {/* The sheet stays compact so the first deals show above it, not under it. */}
      <main className="mx-auto w-full max-w-[640px] px-[18px] pb-[300px] pt-6">{left}</main>
      <div
        className="fixed inset-x-0 bottom-0 z-30 mx-auto flex w-full max-w-[640px] flex-col gap-2 rounded-t-[26px] border-t px-[18px] pb-[max(env(safe-area-inset-bottom),12px)] pt-[14px]"
        style={{ backgroundColor: C.card, borderColor: C.line, boxShadow: "var(--onb-sheet-shadow)" }}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="text-[16px] font-black">
            {view.count > 0 ? t("onboarding2.dp_unlock_all", { count: view.count.toLocaleString("en-US") }) : t("onboarding2.dp_unlock")}
          </span>
          {props.offerPct !== null ? <GreenPill small>{t("onboarding2.dp_offer_short", { pct: props.offerPct })}</GreenPill> : null}
        </div>
        <OnbError error={props.error} />
        <div role="radiogroup" aria-label={t("onboarding2.choose_plan_label")} className="mt-1.5 grid grid-cols-3 gap-2">
          {props.plans.map((plan) => {
            const selected = plan.id === active.id;
            return (
              <button
                key={plan.id}
                type="button"
                role="radio"
                aria-checked={selected}
                aria-label={`${plan.name} ${plan.price}`}
                onClick={() => props.onSelectPlan(plan.id)}
                disabled={props.busy}
                className="relative flex flex-col items-center rounded-xl border-2 px-1 pb-[5px] pt-[8px]"
                style={{ borderColor: selected ? C.selBorder : C.line, backgroundColor: selected ? C.sel : C.card }}
              >
                {plan.id === "pro" ? (
                  <span className="absolute -top-2.5 left-1/2 flex h-[19px] -translate-x-1/2 items-center whitespace-nowrap rounded-full px-[7px] text-[9px] font-black" style={{ backgroundColor: ONB_YELLOW, color: ONB_YELLOW_INK }}>
                    {t("onboarding2.dp_best_value")}
                  </span>
                ) : null}
                <span className="text-[11.5px] font-bold leading-[14px]" style={{ color: C.muted }}>{plan.name}</span>
                <span className="text-[17px] font-black leading-[22px] tracking-[-0.4px]" style={{ color: C.ink }}>{plan.price}</span>
                {plan.wasPrice ? <s className="text-[10.5px] leading-[13px]" style={{ color: C.soft }}>{plan.wasPrice}{t("onboarding2.dp_per_mo")}</s> : <span className="h-[13px]" />}
              </button>
            );
          })}
        </div>
        <span className="line-clamp-2 text-center text-[11.5px] leading-[15px]" style={{ color: C.body }}>
          {active.name}: {active.description}
        </span>
        <YellowCta height={50} fontSize={17} onClick={props.onContinue} busy={props.busy}>
          {props.ctaLabel}
        </YellowCta>
        {freeLink}
        {finePrint}
      </div>
    </OnbPage>
  );
}
